import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { useIMask } from 'react-imask'

import { TextField, TextFieldProps } from '@components/TextField'

export type MaskTextFieldProps = TextFieldProps &
  IMask.AnyMaskedOptions & {
    mask: string
    blocks?: any
    lazy?: boolean
    omitOnComplete?: boolean
    onValueChange?(value: string): void
    onUnmaskedValueChange?(value: string): void
  }

export interface IMaskRefProps {
  getValue(): number
  setValue(value: string | undefined): void
  setUnmaskedValue(value: string): void
}

const ForwardedMaskTextField: ForwardRefRenderFunction<
  IMaskRefProps,
  MaskTextFieldProps
> = (
  {
    mask,
    blocks,
    lazy,
    omitOnComplete,
    onValueChange,
    onUnmaskedValueChange,
    ...rest
  }: MaskTextFieldProps,
  componentRef
) => {
  const onMaskRefValueChange = () => {
    onValueChange?.(maskRef.current?.value ?? '')
    onUnmaskedValueChange?.(maskRef.current?.unmaskedValue ?? '')
  }

  const { ref, maskRef, setValue, setUnmaskedValue } = useIMask(
    {
      mask,
      blocks,
      lazy,
    },
    {
      onAccept: onMaskRefValueChange,
      onComplete: () => {
        if (omitOnComplete) {
          return
        }

        onMaskRefValueChange()
      },
    }
  )

  useImperativeHandle(componentRef, () => {
    return {
      getValue() {
        return Number(maskRef.current?.unmaskedValue ?? '')
      },
      setValue(value: string) {
        if (maskRef.current) {
          maskRef.current.value = value
        }
      },
      setUnmaskedValue(value: string) {
        setUnmaskedValue(value)
        setValue(value)
      },
    }
  })

  return (
    <TextField
      {...rest}
      parentValue={maskRef.current?.value}
      ref={ref as React.MutableRefObject<HTMLInputElement>}
    />
  )
}

const MaskTextField = forwardRef(ForwardedMaskTextField)

export { MaskTextField }
